import { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../../../ui/toast';
import {
  addQuestion,
  deleteQuestion,
  searchPaginatedQuestions,
  updateQuestion,
} from '../../../../utils/api_service/job_api';
import {
  createSkill,
  createSubSkill,
} from '../../../../utils/api_service/skill_api';
import { convertFileToBase64 } from '../../../../utils/helpers/fileHandler';
import { editQuestionRedux } from '../../../../utils/redux_store/features/question_reducer';
import { addNewSkill } from '../../../../utils/redux_store/features/skill_reducer';
import { addNewSubSkill } from '../../../../utils/redux_store/features/subskill_reducer';
import { RootState } from '../../../../utils/redux_store/store';
import {
  Question,
  QuestionSearch,
  SubSkill,
} from '../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';

export const useQuestionBank = () => {
  const dispatch = useDispatch();
  const skillsStore = useSelector((s: RootState) => s.skills);
  const levels = useSelector((s: RootState) => s.skillLevels);
  const jobTitles = useSelector((s: RootState) => s.jobTitles);
  const subSkillsStore = useSelector((s: RootState) => s.subSkills);

  const questionTypes: SelectOptions[] = [
    { value: 'Modern', label: 'Video' },
    { value: 'Classic', label: 'Text' },
  ];
  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchObject, setSearchObject] = useState<QuestionSearch>({
    questionTitle: '',
    skillId: '',
    levelId: '',
    subSkill1Id: '',
    subSkill2Id: '',
    subSkill3Id: '',
    jobTitle: '',
    questionPage: 10,
  });

  // Results & Pagination
  const [searchResults, setSearchResults] = useState<Question[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  // UI State
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [skillList, setSkillList] = useState<SelectOptions[]>(
    skillsStore.skillsList,
  );

  // Accordion state (for non-selected questions)
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [disabledMap, setDisabledMap] = useState<Record<string, boolean>>({});

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const setDisabled = (id: string, disabled: boolean) =>
    setDisabledMap((p) => ({ ...p, [id]: disabled }));

  // Debounced fetch
  useEffect(() => {
    const tid = setTimeout(() => {
      setLoading(true);
      searchPaginatedQuestions({
        ...searchObject,
        questionTitle: searchTerm,
        pageNumber: 1,
      }).then((res) => {
        setSearchResults(res.questions);
        setPageNumber(1);
        setTotalPages(res.pages);
        setTotalQuestions(res.numberOfQuestions);
        setLoading(false);
      });
    }, 500);
    return () => clearTimeout(tid);
  }, [searchTerm, searchObject]);

  // Sync skillList
  useEffect(() => {
    setSkillList(skillsStore.skillsList);
  }, [skillsStore.skillsList]);

  const switchPages = (newPage: number) => {
    setLoading(true);
    searchPaginatedQuestions({
      ...searchObject,
      questionTitle: searchTerm,
      pageNumber: newPage,
    }).then((res) => {
      setPageNumber(newPage);
      setSearchResults(res.questions);
      setTotalQuestions(res.numberOfQuestions);
      setLoading(false);
    });
  };

  // CRUD ops
  const addNewQuestion = (q: Question) => {
    addQuestion(q).then((resp) => {
      if (resp && resp.id) {
        setAddModalOpen(false);
        setSearchResults((p) => [...p, resp]);
      } else {
        const errorMsg =
          resp?.error?.error ||
          resp?.error ||
          (resp?.status === false ? 'Validation Error' : 'Unknown Error');
        toast.error(`Error: ${errorMsg}`, { position: 'bottom-center' });
      }
    });
  };
  const onEditSave = (q: Question) => {
    updateQuestion(q).then((resp) => {
      if (resp.id) {
        setSearchResults((p) => p.map((x) => (x.id === q.id ? q : x)));
        dispatch(editQuestionRedux(q));
        setDisabled(q.id, true);
      }
    });
  };
  const deleteQuestionFromJob = (id: string) => {
    deleteQuestion(id).then((res) => {
      if (res.ok) setSearchResults((p) => p.filter((x) => x.id !== id));
    });
  };

  // — Handlers lifted from CollapsibleQuestion —
  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    // We’ll optimistically save on each field change
    onEditSave({ ...currentQuestion!, [name]: value });
  };

  const handleSelectChange = (selected: SelectOptions | null, action: any) => {
    const { name } = action;
    onEditSave({
      ...currentQuestion!,
      [name]: selected ? selected.value : null,
    });
  };

  const handleMultiSelectChange = (selected: SelectOptions[], action: any) => {
    const values = selected.map((s) => s.value);
    onEditSave({
      ...currentQuestion!,
      [action.name]: values,
    });
  };

  const onAddNewSkill = (
    skill: string,
    parent?: string | null,
    level?: string,
  ) => {
    if (parent) {
      createSubSkill(skill, parent)
        .then((r) => r.ok && r.json())
        .then((newSub: SubSkill) => {
          dispatch(addNewSubSkill(newSub));
          onEditSave({
            ...currentQuestion!,
            subSkill1Id:
              level === '1' ? newSub.id : currentQuestion!.subSkill1Id,
            subSkill2Id:
              level === '2' ? newSub.id : currentQuestion!.subSkill2Id,
            subSkill3Id:
              level === '3' ? newSub.id : currentQuestion!.subSkill3Id,
          });
        });
    } else {
      createSkill(skill)
        .then((r) => r.ok && r.json())
        .then((newSkill) => {
          dispatch(addNewSkill({ value: newSkill.id, label: skill }));
          onEditSave({
            ...currentQuestion!,
            skillId: newSkill.id,
          });
        });
    }
  };

  const addFileViaInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    convertFileToBase64(file).then((base64) => {
      onEditSave({
        ...currentQuestion!,
        mediaQuestion: true,
        mediaExtension: file.name.split('.').pop()!,
        fileEncoded: base64 as string,
      });
    });
  };

  const clearMediaFile = () => {
    onEditSave({
      ...currentQuestion!,
      mediaQuestion: false,
      mediaExtension: null,
      fileEncoded: null,
    });
  };

  // We’ll use this inside render to know which question is “current”
  let currentQuestion: Question | null = null;
  return {
    skillList,
    levels,
    subSkillsStore,
    jobTitles,
    searchObject,
    setSearchObject,
    searchTerm,
    setSearchTerm,
    setAddModalOpen,
    searchResults,
    onEditSave,
    deleteQuestionFromJob,
    setSkillList,
    currentQuestion,
    expandedIds,
    toggleExpand,
    disabledMap,
    setDisabled,
    handleChange,
    handleSelectChange,
    handleMultiSelectChange,
    onAddNewSkill,
    questionTypes,
    pageNumber,
    totalPages,
    totalQuestions,
    switchPages,
    addModalOpen,
    addNewQuestion,
    loading,
    addFileViaInput,
    clearMediaFile,
  };
};
